import React, { useEffect, useMemo } from 'react';

import { lunaStreamsConstants } from '@vlabs/api-bindings/src/luna-streams';
import validate from '@vlabs/shared/validators';
import { Control, Fold, SettingsItemWrapper } from '@vlabs/uikit';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './SharedSubFormStyles.module.sass';

export function DataSubForm() {
  const { t } = useTranslation();
  const {
    watch, getValues, reset, setValue,
    register, control, formState: { errors },
  } = useFormContext();

  const dataType = watch('data.type')?.value;

  useEffect(() => {
    if (!['tcp', 'udp'].includes(dataType)) {
      setValue('data.endless', undefined);
    }
  }, [dataType, setValue]);

  const isImageDataType = dataType === 'images';

  useEffect(() => {
    const sub = watch((_, { name }) => {
      if (name === 'data.type' && !isImageDataType) {
        const newValues = getValues();
        newValues.data.mask = '';

        reset(newValues);
      }
    });

    return () => sub.unsubscribe();
    // eslint-disable-next-line
  }, [watch]);

  const videoInfo = watch('video_info');
  const sourceDimensions = useMemo(() => {
    if (videoInfo?.width !== undefined && videoInfo?.height !== undefined) {
      return {
        width: videoInfo.width,
        height: videoInfo.height,
      };
    }

    return undefined;
  }, [videoInfo]);

  return (
    <Fold isOpen title={t('lunaStreamsStreams:form.data.title')}>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.data.type')}>
        <Control.Select
          control={control}
          name="data.type"
          options={lunaStreamsConstants.STREAM_TYPE_OPTIONS.raw}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper title={t('lunaStreamsStreams:form.data.reference')}>
        <Control.Input
          errors={errors}
          {...register('data.reference', {
            required: validate.required(),
            maxLength: validate.maxLength(512),
          })}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper title={t('lunaStreamsStreams:form.data.ROI.label')}>
        <Controller
          control={control}
          name="data.roi"
          render={({ field: { value, onChange } }) => {
            return (
              <Control.CoordinatesInput
                errors={errors}
                name="data.roi"
                onChange={onChange}
                previewImage={watch('preview.last_frame.uiUrl')}
                sourceDimensions={sourceDimensions}
                texts={{
                  title: t('lunaStreamsStreams:form.data.ROI.CoordinatesInput.Modal.title'),
                }}
                value={value}
              />
            );
          }}
          rules={{ validate: validate.region }}
        />

      </SettingsItemWrapper>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.data.DROI.label')}>
        <Controller
          control={control}
          name="data.droi"
          render={({ field: { value, onChange } }) => {
            return (
              <Control.CoordinatesInput
                errors={errors}
                name="data.droi"
                onChange={onChange}
                previewImage={watch('preview.last_frame.uiUrl')}
                sourceDimensions={sourceDimensions}
                texts={{
                  title: t('lunaStreamsStreams:form.data.ROI.CoordinatesInput.Modal.title'),
                }}
                value={value}
              />
            );
          }}
          rules={{ validate: validate.region }}
        />

      </SettingsItemWrapper>

      <SettingsItemWrapper title={t('lunaStreamsStreams:form.data.rotation')}>
        <Control.Select
          control={control}
          name="data.rotation"
          options={lunaStreamsConstants.ROTATION_OPTIONS.raw}
        />
      </SettingsItemWrapper>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.data.preferred_program_stream_frame_width')}>
        <Control.Input
          {...register('data.preferred_program_stream_frame_width', {
            min: validate.gte(0),
            max: validate.lte(32767),
            validate: validate.integer(),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>

      {isImageDataType && (
        <SettingsItemWrapper
          title={t('lunaStreamsStreams:form.data.mask')}
        >
          <Control.Input
            errors={errors}
            {...register('data.mask', {
              required: validate.required(),
              maxLength: validate.maxLength(128),
            })}
          />
        </SettingsItemWrapper>
      )}

      {(dataType === 'tcp' || dataType === 'udp') && (
        <SettingsItemWrapper
          data-tooltip-content={t('lunaStreamsStreams:form.data.endless.tooltip')}
          id="data.endless"
          // FIXME: Для SettingItemWrapper нужен пропс для стилизации модального окна, через
          // commonStylesProps не получается долезть
          modalContent={(
            <div className={st.ModalContent}>
              {t('lunaStreamsStreams:form.data.endless.tooltip')}
            </div>
          )}
          title={t('lunaStreamsStreams:form.data.endless.label')}
        >
          <Control.Switch
            control={control}
            name="data.endless"
          />
        </SettingsItemWrapper>
      )}
    </Fold>
  );
}
